// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import { Button, Grid, Typography } from '@mui/material'

// ** Child Imports
import { WidgetType, UTMType, AttributionType, SalesReportFilterType } from 'src/lib/types/dashboardAnalytics'
import { arrayToCsv, downloadCsvHelper, numberWithCommas } from 'src/lib/utils/helpers'
import { useFilter } from 'src/lib/contexts/FilterContext'
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import { clickWiseAttributionOptions, saleByWidgetFilterOptions } from 'src/assets/AnalyticsConfig'
import { extractDateFromFilters, getAnalyticsWidgetPayload } from 'src/lib/utils/analyticsHelpers'
import { analyticsConstants } from 'src/lib/utils/constants'
import { ArrowDownRight, ArrowUpRight, Download, Minus, TrendingDown, TrendingUp } from 'react-feather'
import ExportComponent from 'src/components/common/export-component/ExportComponent'
import { ExportOptionConfigType } from 'src/lib/types/exports'
import { addDays, startOfDay } from 'date-fns'
import { ClickWiseAttributionsType, OverviewWidgetFilterOptionsType } from '../../../lib/types/dashboardAnalytics'
import { useLocale } from 'src/lib/hooks/useLocal'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import GenericTable from 'src/components/common/table/GenericTable'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'
import {
  FilterDropdown,
  FilterConfig,
  RangeValue
} from 'src/components/common/search-and-filters/subcomponents/FilterDropdown'

type SalesContributorsTableProps = WidgetType & {
  enableHeaderFilter?: boolean
  headerFilterConfigMap?: Partial<Record<SalesReportFilterType, FilterConfig<UTMType>>>
  headerGetRangeInitialValue?: () => RangeValue
  headerOnRangeApply?: (filterType: SalesReportFilterType, range: RangeValue) => void
  headerIsOptionChecked?: (filterType: SalesReportFilterType, value: UTMType) => boolean
  headerOnOptionToggle?: (filterType: SalesReportFilterType, value: UTMType) => void
  headerGetSearchInitialValue?: (filterType: SalesReportFilterType) => { selectedOption?: UTMType; searchText: string }
  headerOnSearchApply?: (filterType: SalesReportFilterType, selectedOption: UTMType, searchText: string) => void
  headerFilterChips?: React.ReactNode
}

const SalesContributorsTable: React.FC<SalesContributorsTableProps> = ({
  value,
  hideDownloadReport = true,
  hideSalesByDropdown = false,
  isLoading,
  error,
  triggerRefresh,
  triggerDownloadReport,
  downloadTriggered,
  hideBorder = false,
  hideRowDivider = false,
  isPaginationEnabled = true,
  hideHeader = false,
  hideFooter = false,
  removeCellPadding = false,
  tableVariant = 'table',
  isReportView = false,
  UTMType = 'source',
  setAttributionType,
  moreIsGood,
  hasComparison,
  customFilters,
  enableHeaderFilter = false,
  headerFilterConfigMap,
  headerGetRangeInitialValue,
  headerOnRangeApply,
  headerIsOptionChecked,
  headerOnOptionToggle,
  headerGetSearchInitialValue,
  headerOnSearchApply,
  headerFilterChips
}) => {
  const { analyticsV2Filters } = useFilter()
  const { t } = useLocale()
  const { sendActivityLog } = useActivityLogs()
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [selectedUTMType, setSelectedUTMType] = useState<UTMType>('source')
  const [selectedAtrributionType, setSelectedAttributionType] = useState<AttributionType>('LAST_CLICK')
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  })
  const newrowsPerPage = pagination.pageSize
  const newpage = pagination.pageIndex

  const activityLogPayload = {
    entityName: EntityName.OVERVIEW,
    actionName: 'downloaded report'
  }

  const exportOptionData: ExportOptionConfigType[] = [
    {
      valueType: 'orders_utm',
      label: 'Order wise last click attribution'
    },
    {
      valueType: 'orders_utm_first_click',
      label: 'Order wise first click attribution'
    }
  ]

  useEffect(() => {
    if (!UTMType) return
    setSelectedUTMType(UTMType)
  }, [UTMType, newrowsPerPage, newpage])

  useEffect(() => {
    // keep pagination state in sync with table state
    setPagination(p => ({ ...p, pageIndex: newpage, pageSize: newrowsPerPage }))
  }, [newrowsPerPage, newpage])

  useEffect(() => {
    if (typeof value !== 'undefined') setPagination(p => ({ ...p, pageIndex: 0 }))
  }, [value])

  const handleChangeRowsPerPage = (pageSize: number) => {
    triggerRefresh(
      getAnalyticsWidgetPayload(
        selectedAtrributionType === 'LAST_CLICK'
          ? analyticsConstants.FLO_SALES_WIDGET
          : analyticsConstants.FLO_UTM_FIRST_CLICK_SALES_WIDGET,
        extractDateFromFilters(analyticsV2Filters, 'startDate'),
        extractDateFromFilters(analyticsV2Filters, 'endDate'),
        undefined,
        selectedUTMType as UTMType,
        customFilters
      )
    )
    setPagination(p => ({ ...p, pageSize: newrowsPerPage, pageIndex: 0 }))
  }

  useEffect(() => {
    if (downloadTriggered) {
      downloadCsvHelper(arrayToCsv(value, ['UTM', 'Revenue', 'Order count']), 'utm_report.csv')
      typeof triggerDownloadReport !== 'undefined' && triggerDownloadReport(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadTriggered])

  const columns: ColumnDef<any>[] = [
    {
      header: 'salesBy',
      cell: ({ row }: { row: any }) => {
        const value = row.original['salesBy']
        const sececondayValue = row.original['ordersCount']

        return (
          <div className='flex flex-col w-52'>
            <Typography
              variant='body2'
              sx={{ color: 'text.primary', lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {value === '' ? 'Direct' : value}
            </Typography>
            <Typography variant='body2' sx={{ color: '#949494', lineHeight: '18px' }}>
              {numberWithCommas(sececondayValue, 0)} orders
            </Typography>
          </div>
        )
      }
    },
    {
      header: 'revenue',
      cell: ({ row }: { row: any }) => {
        const value = row.original['revenue']
        return (
          <p
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'start',
              margin: 0,
              fontWeight: 500,
              color: '#4D4D4D'
            }}
          >
            <span>{`\u20B9`}</span>
            <span>{numberWithCommas(value)}</span>
          </p>
        )
      }
    },
    {
      header: hasComparison ? 'compareToVal' : '',
      maxSize: hasComparison ? 70 : 0,
      minSize: hasComparison ? 70 : 0,
      id: 'compareToVal',
      cell: ({ row }: { row: any }) => {
        const value = row.original['compareToVal']
        const isGood = moreIsGood ? Boolean(parseInt(value) > 0) : Boolean(parseInt(value) < 0)

        return hasComparison ? (
          <p
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              gap: 2,
              margin: 0,
              fontWeight: 600,
              color: isGood ? '#2C874A' : '#CB2711'
            }}
          >
            {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
              isGood ? (
                <ArrowUpRight className='h-4 w-4 stroke-[2.5px]' />
              ) : (
                <ArrowDownRight className='h-4 w-4 stroke-[2.5px]' />
              )
            ) : (
              <></>
            )}
            {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
              <> {Math.sign(parseInt(value)) === -1 ? value?.slice(1) : value}</>
            ) : (
              <Minus className='text-[#949494] w-[14px] stroke-[2.5px]' />
            )}
          </p>
        ) : (
          <></>
        )
      }
    }
  ]

  return (
    <div className='flex flex-col w-full space-y-4'>
      <Grid item sx={{ width: '100%', position: 'relative' }}>
        {!Boolean(hideDownloadReport) ? (
          <div className='flex gap-4 w-1/2 justify-end items-center absolute -top-[81px] right-0'>
            <Button
              variant='outlined'
              sx={{ height: 36 }}
              disabled={!value?.length}
              onClick={() => {
                downloadCsvHelper(arrayToCsv(value, ['UTM', 'Revenue', 'Order count']), 'utm_report.csv')
                sendActivityLog(activityLogPayload)
              }}
            >
              <Download style={{ transform: 'scale(0.75)' }} />
              {t('download_report')}
            </Button>
          </div>
        ) : (
          <></>
        )}
        <div className='absolute -top-[81px] right-[176px]'>
          {isReportView ? (
            <ExportComponent
              isExportDisabled={false}
              globalEndDate={addDays(new Date(), 1)}
              exportOptionData={exportOptionData}
              globalStartDate={startOfDay(new Date())}
              titleText={'Export orders data '}
              jobButtonText={'Generate'}
              dialogButtonText={'Export'}
              hideRadioButtons={false}
            />
          ) : (
            <></>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: hideSalesByDropdown ? 'end' : 'start',
            gap: 12,
            ...(Boolean(hideSalesByDropdown) ? { position: 'absolute' } : ''),
            top: -81,
            right: 278
          }}
        >
          {!Boolean(hideSalesByDropdown) ? (
            <DropdownButton
              options={
                selectedAtrributionType === 'FIRST_CLICK'
                  ? saleByWidgetFilterOptions?.filter(
                      (option: OverviewWidgetFilterOptionsType) => option.value !== 'landing_page_url'
                    )
                  : saleByWidgetFilterOptions
              }
              handleClick={(index: number) => {
                if (hasComparison) {
                  triggerRefresh(
                    getAnalyticsWidgetPayload(
                      selectedAtrributionType === 'LAST_CLICK'
                        ? analyticsConstants.FLO_SALES_WIDGET
                        : analyticsConstants.FLO_UTM_FIRST_CLICK_SALES_WIDGET,
                      extractDateFromFilters(analyticsV2Filters, 'startDate'),
                      extractDateFromFilters(analyticsV2Filters, 'endDate'),
                      undefined,
                      saleByWidgetFilterOptions[index]?.value as UTMType,
                      customFilters
                    ),
                    getAnalyticsWidgetPayload(
                      selectedAtrributionType === 'LAST_CLICK'
                        ? analyticsConstants.FLO_SALES_WIDGET
                        : analyticsConstants.FLO_UTM_FIRST_CLICK_SALES_WIDGET,
                      extractDateFromFilters(analyticsV2Filters, 'compareToStartDate'),
                      extractDateFromFilters(analyticsV2Filters, 'compareToEndDate'),
                      undefined,
                      saleByWidgetFilterOptions[index]?.value as UTMType,
                      customFilters
                    )
                  )
                } else {
                  triggerRefresh(
                    getAnalyticsWidgetPayload(
                      selectedAtrributionType === 'LAST_CLICK'
                        ? analyticsConstants.FLO_SALES_WIDGET
                        : analyticsConstants.FLO_UTM_FIRST_CLICK_SALES_WIDGET,
                      extractDateFromFilters(analyticsV2Filters, 'startDate'),
                      extractDateFromFilters(analyticsV2Filters, 'endDate'),
                      undefined,
                      saleByWidgetFilterOptions[index]?.value as UTMType,
                      customFilters
                    )
                  )
                }
                setSelectedUTMType(saleByWidgetFilterOptions[index]?.value as UTMType)
              }}
              variant='outlined'
            />
          ) : (
            <></>
          )}
          <DropdownButton
            options={
              selectedUTMType === 'landing_page_url'
                ? clickWiseAttributionOptions.filter(
                    (option: ClickWiseAttributionsType) => option.value === 'LAST_CLICK'
                  )
                : clickWiseAttributionOptions
            }
            handleClick={(index: number) => {
              triggerRefresh(
                getAnalyticsWidgetPayload(
                  clickWiseAttributionOptions[index]?.value === 'LAST_CLICK'
                    ? analyticsConstants.FLO_SALES_WIDGET
                    : analyticsConstants.FLO_UTM_FIRST_CLICK_SALES_WIDGET,
                  extractDateFromFilters(analyticsV2Filters, 'startDate'),
                  extractDateFromFilters(analyticsV2Filters, 'endDate'),
                  undefined,
                  selectedUTMType,
                  customFilters
                )
              )
              typeof setAttributionType !== 'undefined' && setAttributionType(clickWiseAttributionOptions[index]?.value)
              setSelectedAttributionType(clickWiseAttributionOptions[index]?.value)
            }}
            variant='outlined'
            size={Boolean(hideSalesByDropdown) ? 'large' : 'small'}
          />
          {enableHeaderFilter && headerFilterConfigMap ? (
            <div className='flex items-center'>
              <FilterDropdown<SalesReportFilterType, UTMType>
                enableFilter={true}
                filterConfigMap={headerFilterConfigMap}
                getRangeInitialValue={headerGetRangeInitialValue}
                onRangeApply={headerOnRangeApply}
                isOptionChecked={headerIsOptionChecked}
                onOptionToggle={headerOnOptionToggle}
                getSearchInitialValue={headerGetSearchInitialValue}
                onSearchApply={headerOnSearchApply}
              />
            </div>
          ) : null}
        </div>
      </Grid>
      {headerFilterChips ? <div className='w-full'>{headerFilterChips}</div> : null}
      <GenericTable
        data={
          isReportView
            ? [...value?.slice(newpage * newrowsPerPage, newpage * newrowsPerPage + newrowsPerPage)]
            : [...value?.slice(newpage * 5, newpage * 5 + 5)]
        }
        columns={columns}
        pagination={{
          paginationState: pagination,
          setPaginationState: setPagination
        }}
        totalRows={value?.length ?? 0}
        loading={isLoading}
        customPaginationConfig={{
          customPagination: false
        }}
        isCustomizable={{
          isFullWidth: false,
          isResizable: false,
          hidePaginationButton: !isReportView,
          hideDropDown: !isReportView,
          hideHeaders: !isReportView
        }}
      />
    </div>
  )
}

export default SalesContributorsTable
